import { useEffect, useState } from "react";
import { fetchBscStats } from "../../api/client";
import type { BscStatsResponse } from "../../api/types";

const kpiGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
  gap: "var(--space-3)",
};

export function BscStats() {
  const [stats, setStats] = useState<BscStatsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchBscStats(30)
      .then((s) => { if (!cancelled) setStats(s); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (isLoading) {
    return (
      <div className="loading-center" style={{ minHeight: 400 }}>
        <div className="spinner spinner-lg" />
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="alert alert-danger">
        <span className="material-symbols-outlined" style={{ fontSize: 16, marginRight: 4 }}>error</span>
        Errore: {error || "Dati non disponibili"}
      </div>
    );
  }

  const statusCards = [
    { key: "pending_review", label: "In revisione", icon: "pending", color: "var(--indaco-warning)", value: stats.pending_review },
    { key: "approved", label: "Approvate", icon: "check_circle", color: "var(--indaco-success)", value: stats.approved },
    { key: "rejected", label: "Rifiutate", icon: "cancel", color: "var(--indaco-danger)", value: stats.rejected },
    { key: "error", label: "Errori", icon: "error", color: "var(--indaco-gray-600)", value: stats.error },
  ];

  const maxDaily = Math.max(...stats.by_date.map((d) => d.count), 1);
  const maxVessel = Math.max(...stats.top_vessels.map((v) => v.count), 1);
  const maxCompl = Math.max(...Object.values(stats.by_completeness), 1);
  const complBuckets = ["0-50%", "50-70%", "70-85%", "85-95%", "95-100%"];

  return (
    <div>
      <h1 className="page-title" style={{ marginBottom: "var(--space-6)" }}>
        <span className="material-symbols-outlined" style={{ fontSize: 24, marginRight: 8, verticalAlign: "middle" }}>bar_chart</span>
        Statistiche BSC
      </h1>

      <div style={kpiGridStyle}>
        <div className="card" style={{ textAlign: "center" }}>
          <div style={{ fontSize: "var(--font-size-2xl)", fontWeight: "var(--font-weight-bold)", color: "var(--indaco-brand-primary)" }}>
            {stats.total}
          </div>
          <div className="text-sm text-muted">Totale pratiche</div>
        </div>
        {statusCards.map((sc) => (
          <div key={sc.key} className="card" style={{ textAlign: "center" }}>
            <div style={{ fontSize: "var(--font-size-2xl)", fontWeight: "var(--font-weight-bold)", color: sc.color }}>
              {sc.value}
            </div>
            <div className="text-sm text-muted" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16, color: sc.color }}>{sc.icon}</span>
              {sc.label}
            </div>
          </div>
        ))}
        <div className="card" style={{ textAlign: "center" }}>
          <div style={{ fontSize: "var(--font-size-2xl)", fontWeight: "var(--font-weight-bold)", color: "var(--indaco-success)" }}>
            {stats.approved_today}
          </div>
          <div className="text-sm text-muted">Approvate oggi</div>
        </div>
      </div>

      {stats.by_date.length > 0 && (
        <div className="card" style={{ marginTop: "var(--space-6)" }}>
          <h3 style={{ fontSize: "var(--font-size-md)", marginBottom: "var(--space-4)" }}>
            Pratiche processate ultimi {stats.period_days} giorni
          </h3>
          <div style={{ display: "flex", alignItems: "flex-end", gap: "2px", height: 120, overflowX: "auto" }}>
            {stats.by_date.map((d) => (
              <div
                key={d.date}
                title={`${d.date}: ${d.count}`}
                style={{
                  flex: 1, minWidth: 8, maxWidth: 24,
                  height: `${(d.count / maxDaily) * 100}%`,
                  background: "var(--indaco-brand-primary)",
                  borderRadius: "2px 2px 0 0",
                  transition: "height var(--transition-normal)",
                }}
              />
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: "var(--space-2)" }}>
            <span className="text-xs text-muted">{stats.by_date[0]?.date}</span>
            <span className="text-xs text-muted">{stats.by_date[stats.by_date.length - 1]?.date}</span>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "var(--space-4)", marginTop: "var(--space-4)" }}>
        {stats.top_vessels.length > 0 && (
          <div className="card">
            <h3 style={{ fontSize: "var(--font-size-md)", marginBottom: "var(--space-4)" }}>Top navi (per volume)</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
              {stats.top_vessels.map((v) => (
                <div key={v.label}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                    <span className="text-sm" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginRight: 8 }}>
                      {v.label}
                    </span>
                    <span className="text-sm fw-semibold">{v.count}</span>
                  </div>
                  <div className="progress-bar">
                    <div className="progress-bar-fill" style={{ width: `${(v.count / maxVessel) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {Object.keys(stats.by_completeness).length > 0 && (
          <div className="card">
            <h3 style={{ fontSize: "var(--font-size-md)", marginBottom: "var(--space-4)" }}>Distribuzione completeness</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
              {complBuckets.map((bucket) => {
                const n = stats.by_completeness[bucket] ?? 0;
                return (
                  <div key={bucket}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                      <span className="text-sm">{bucket}</span>
                      <span className="text-sm fw-semibold">{n}</span>
                    </div>
                    <div className="progress-bar">
                      <div className="progress-bar-fill" style={{ width: `${(n / maxCompl) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
